import React, { useState, useCallback, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import { TransportFormData } from "@tripbuilder/cat-transport/right/customTransportComponents/transportTypes";
import DateTimeSelector from "@tripbuilder/cat-transport/right/customTransportComponents/dateTimeComp";
import MultiProviderSelector from "@tripbuilder/cat-transport/right/customTransportComponents/providerSelector";
import StopoverSelector from "@tripbuilder/cat-transport/right/customTransportComponents/stopOverSelector";
import TransportPreview from "@tripbuilder/cat-transport/right/customTransportComponents/transportPreview";
import CostSelector from "@tripbuilder/cat-transport/right/customTransportComponents/costSection";
import { TransportData, Trip, TripTransport, TransportLeg } from "@/types/trip";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import { ProgressBar } from "@tripbuilder/common/ProgressBar";
import { BackButton, NextButton } from "@tripbuilder/common/custom/Buttons";

interface TransportFormProps {
  transportData: TransportData;
  onSubmit: (transport: TripTransport) => void;
  tripData: Trip;
}

const TransportForm: React.FC<TransportFormProps> = ({
  transportData,
  onSubmit,
  tripData,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<TransportFormData>({
    numberOfLegs: "",
    legs: [],
  });
  const [currentLegIndex, setCurrentLegIndex] = useState(0);
  const [departureTime, setDepartureTime] = useState<string | null>(null);
  const [arrivalTime, setArrivalTime] = useState<string | null>(null);
  const { toast } = useToast();

  const handleInputChange = useCallback(
    (field: string, value: any) => {
      if (field === "numberOfLegs") {
        if (value === "") {
          setFormData((prev) => ({
            ...prev,
            numberOfLegs: "",
            legs: [],
          }));
        } else {
          const numLegs = parseInt(value);
          if (!isNaN(numLegs)) {
            setFormData((prev) => ({
              ...prev,
              numberOfLegs: numLegs,
              legs: Array(numLegs)
                .fill(null)
                .map(() => ({
                  mode: "flight" as const,
                  departureTime: "",
                  arrivalTime: "",
                  cost: 0,
                  origin: "",
                  destination: "",
                })),
            }));
          }
        }
      } else if (field === "legs") {
        setFormData((prev) => ({
          ...prev,
          legs: value,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          legs: prev.legs.map((leg, i) =>
            i === currentLegIndex ? { ...leg, [field]: value } : leg
          ),
        }));
      }
    },
    [currentLegIndex]
  );

  // Memoized questions array based on formData
  const questions = useMemo(
    () => [
      {
        id: "numberOfLegs",
        question: "How many legs of transport are required for this journey?",
        type: "number",
      },
      {
        id: "stopovers",
        question:
          Number(formData.numberOfLegs) === 1
            ? "Does this look right?"
            : "Where would you like to stop along the way?",
        type: "stopovers",
      },
      {
        id: "providers",
        question: "Who are you travelling with?",
        type: "multiProvider",
      },
      {
        id: "times",
        question: "When are you travelling?",
        type: "datetime",
      },
      {
        id: "cost",
        question: "What is the cost?",
        type: "cost",
      },
    ],
    [formData.numberOfLegs]
  );

  const renderInputField = () => {
    const currentQuestion = questions[currentStep];
    if (!currentQuestion) return null;

    const currentLeg = formData.legs[currentLegIndex] || {
      departureTime: "",
      arrivalTime: "",
      provider: "",
      destination: "",
      cost: 0,
    };

    switch (currentQuestion.type) {
      case "number":
        return (
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            placeholder="Enter number of legs (1-5)"
            value={formData.numberOfLegs}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^[1-5]$/.test(value)) {
                handleInputChange("numberOfLegs", value);
              }
            }}
            className="w-full max-w-xs text-sm focus-visible:outline-none focus-visible:ring-0 rounded-lg"
          />
        );

      case "stopovers":
        return (
          <StopoverSelector
            formData={formData}
            handleInputChange={handleInputChange}
            transportData={transportData}
          />
        );

      case "multiProvider":
        return (
          <MultiProviderSelector
            formData={formData}
            currentLegIndex={currentLegIndex}
            handleInputChange={handleInputChange}
            setCurrentLegIndex={setCurrentLegIndex}
          />
        );

      case "datetime":
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg p-2">
                Departure time from {transportData.locationAbove?.name}
              </p>
              <DateTimeSelector
                selectedDate={departureTime || ""}
                onDateSelect={setDepartureTime}
                label="Select departure time"
                targetZone={
                  transportData.locationAbove?.date.time_zone || "UTC"
                }
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg p-2">
                Arrival time into {transportData.locationBelow?.name}
              </p>
              <DateTimeSelector
                selectedDate={arrivalTime || ""}
                onDateSelect={setArrivalTime}
                label="Select arrival time"
                isArrival={true}
                targetZone={
                  transportData.locationBelow?.date.time_zone || "UTC"
                }
              />
            </div>
          </div>
        );

      case "cost":
        return (
          <CostSelector
            formData={formData}
            currentLegIndex={currentLegIndex}
            handleInputChange={handleInputChange}
            setCurrentLegIndex={setCurrentLegIndex}
          />
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    const currentQuestion = questions[currentStep];
    if (!currentQuestion) return false;

    switch (currentQuestion.type) {
      case "number":
        return (
          formData.numberOfLegs !== "" && Number(formData.numberOfLegs) > 0
        );
      case "multiProvider":
        return formData.legs.every((leg) => leg.provider);
      case "stopovers":
        return formData.legs.every((leg, index) => {
          if (index === 0) return true;
          if (index === formData.legs.length - 1) return true;
          return leg.destination;
        });
      case "datetime":
        return !!departureTime && !!arrivalTime;
      case "cost":
        return (
          formData.legs[currentLegIndex]?.cost !== undefined &&
          formData.legs[currentLegIndex]?.cost > 0
        );
      default:
        return true;
    }
  };

  const calculateDuration = (
    departureTime: string,
    arrivalTime: string,
    timezone1: string,
    timezone2: string
  ) => {
    const departure = DateTime.fromISO(departureTime, {
      zone: timezone1,
    });
    const arrival = DateTime.fromISO(arrivalTime, {
      zone: timezone2,
    });

    // Convert both to UTC before calculating duration
    const durationMilliseconds = arrival
      .toUTC()
      .diff(departure.toUTC())
      .as("milliseconds");
    const durationMinutes = Math.round(durationMilliseconds / (1000 * 60));
    return durationMinutes;
  };

  const handleNext = () => {
    if (!isStepValid()) {
      toast({
        title: "Required field",
        description: "Please fill in all required fields before continuing",
        variant: "destructive",
      });
      return;
    }

    if (currentStep < questions.length - 1) {
      if (currentStep === 2) {
        const allProvidersSelected = formData.legs.every((leg) => leg.provider);
        if (!allProvidersSelected) return;
      }
      setCurrentStep((prev) => prev + 1);
    } else {
      if (!departureTime || !arrivalTime) {
        throw new Error("Departure and arrival times must be set");
      }

      const transportLegs: TransportLeg[] = formData.legs.map((leg, index) => {
        if (!departureTime || !arrivalTime) {
          throw new Error("Failed to convert times to UTC");
        }

        // Calculate duration using the UTC times
        const duration = calculateDuration(
          departureTime,
          arrivalTime,
          transportData.locationAbove!.date.time_zone,
          transportData.locationBelow!.date.time_zone
        );

        // Get coordinates based on the leg position
        const getDepartureCoordinates = () => {
          if (index === 0) {
            return transportData.locationAbove?.coordinates;
          }
          return leg.originCoordinates;
        };

        const getArrivalCoordinates = () => {
          if (index === formData.legs.length - 1) {
            return transportData.locationBelow?.coordinates;
          }
          return leg.destinationCoordinates;
        };

        const departureCoordinates = getDepartureCoordinates();
        const arrivalCoordinates = getArrivalCoordinates();

        if (!departureCoordinates || !arrivalCoordinates) {
          console.warn(`Missing coordinates for leg ${index}`);
        }

        return {
          id: uuidv4(),
          mode:
            leg.mode === "flight" ? "flight" : (leg.mode as "train" | "bus"),
          provider: {
            name: leg.providerName || "Unknown Provider",
            type: leg.mode === "flight" ? "airline" : leg.mode,
            code: leg.provider || "UNK",
          },
          departure: {
            location: {
              name: leg.origin || transportData.locationAbove?.name || "",
              code: leg.originCode || "",
              coordinates: departureCoordinates || {
                latitude: 0,
                longitude: 0,
              },
              timezone: transportData.locationAbove?.date.time_zone || "UTC",
            },
            time: {
              utc: departureTime,
            },
          },
          arrival: {
            location: {
              name: leg.destination || transportData.locationBelow?.name || "",
              code: leg.destinationCode || "",
              coordinates: arrivalCoordinates || {
                latitude: 0,
                longitude: 0,
              },
              timezone: transportData.locationBelow?.date.time_zone || "UTC",
            },
            time: {
              utc: arrivalTime,
            },
          },
          duration: duration,
        };
      });

      const newTransport: TripTransport = {
        id: uuidv4(),
        fromLocationId: transportData.locationAbove?.id || "",
        toLocationId: transportData.locationBelow?.id || "",
        type: "custom",
        legs: transportLegs,
        total_price: formData.legs.reduce(
          (total, leg) => total + (leg.cost || 0),
          0
        ),
        currency: tripData.currency || "USD",
        bookingStatus: "booked",
      };

      onSubmit(newTransport);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const isNextDisabled = !isStepValid();

  return (
    <div className="h-full w-full rounded-md">
      <div className="h-full flex flex-col">
        {/* Main content area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="sticky top-0 bg-white w-full mx-auto pt-2 pb-3 z-10">
            <ProgressBar
              currentStep={currentStep}
              totalSteps={questions.length}
            />
          </div>

          <div className="flex-1 overflow-auto">
            <div className="flex items-start">
              <div className="w-full mx-auto 2xl:px-0 px-2 2xl:pr-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mt-2"
                  >
                    <div className="space-y-5">
                      <div className="space-y-1">
                        <h2 className="text-md font-medium text-neutral-800">
                          {currentStep < questions.length &&
                          questions[currentStep]
                            ? questions[currentStep].question
                            : ""}
                        </h2>
                      </div>

                      <div className="h-full">{renderInputField()}</div>

                      <div className="flex justify-between items-center pb-1">
                        {currentStep > 0 && <BackButton onClick={handleBack} />}
                        <NextButton
                          onClick={handleNext}
                          disabled={isNextDisabled}
                          isLastStep={currentStep === questions.length - 1}
                          className={`${currentStep === 0 ? "ml-auto" : ""}`}
                        />
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-md rounded-md px-2 py-1 bg-indigo-50 text-indigo-700 font-medium">
              Preview
            </p>
            <span className="text-xs text-neutral-500">
              See how your transport will appear in your itinerary
            </span>
          </div>
          <div className="border-neutral-200 border rounded-lg p-3 bg-white">
            <TransportPreview
              formData={formData}
              departureTime={departureTime || ""}
              arrivalTime={arrivalTime || ""}
              transportData={transportData}
              tripData={{
                id: "",
                name: "",
                locations: [],
                collaborators: [],
                liked_experiences: [],
                version: 0,
                users: [],
                transport: [],
                currency: "",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransportForm;
